import { Button } from '@neo/primitives/button'
import { MoreVertical } from 'lucide-react'
import type { RUserSession } from 'v2/types/api/auth'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import { IntegrationState } from '../utils/constants'
import { ActionsPopover } from './actions-popover'
import ShareConnectionButton from './share-connection/share-connection-button'

export interface ExternalAppRow extends RExternalOrgApp {
  [key: string]: unknown
  isGroup: boolean
  subRows?: ExternalAppRow[]
}

interface ExternalAppActionsProps {
  app: ExternalAppRow
  currentUser?: RUserSession
}

export const ExternalAppActions = ({ app, currentUser }: ExternalAppActionsProps) => {
  const { isGroup, subRows } = app

  if (isGroup) {
    // Sibling connections of one tool expose the same actions, so the first live one is enough.
    const getFirstAvailableConnection = () => {
      if (!Array.isArray(subRows)) return null
      return (
        subRows.find((connection) => connection.state?.toUpperCase() === IntegrationState.ACTIVE) ||
        null
      )
    }

    const availableConnection = getFirstAvailableConnection()
    const directActions = availableConnection?.actions || []
    const unavailableReason =
      'This integration has no active connection. Reconnect it to view its actions.'

    return (
      <div className="relative flex justify-end items-center gap-2">
        <ActionsPopover appName={app.name} directActions={directActions}>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-3 text-secondary"
            onClick={(e) => {
              e.stopPropagation()
            }}
            data-testid="view-actions-button"
            disabled={!availableConnection}
            disabledReason={availableConnection ? undefined : unavailableReason}
          >
            {availableConnection ? 'View Actions' : 'No active connection'}
          </Button>
        </ActionsPopover>
      </div>
    )
  }

  const isCurrentUserOwner = currentUser?.aom_uuid === app?.integrated_by?.id

  if (!isCurrentUserOwner) return null

  return (
    <div className="relative flex justify-end items-center gap-2">
      <ShareConnectionButton
        app={app}
        className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 [tr:hover_&]:opacity-100"
      />
      <Button
        variant="ghost"
        tooltipContent="Open connection actions"
        tabIndex={-1}
        className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 [.context-menu-open_&]:opacity-100"
        data-testid="button-open-connection-actions"
        onClick={(e) => {
          e.stopPropagation()
          const rect = e.currentTarget.getBoundingClientRect()
          const customEvent = new MouseEvent('contextmenu', {
            bubbles: true,
            cancelable: true,
            clientX: rect.left + 30,
            clientY: rect.bottom,
          })
          e.currentTarget.dispatchEvent(customEvent)
        }}
      >
        <MoreVertical size={20} />
        <span className="sr-only">Open menu</span>
      </Button>
    </div>
  )
}
